import { motion, AnimatePresence } from 'framer-motion';
import { Section } from '../data/portfolioData';
import SkillCard from './SkillCard';
import ProjectCard from './ProjectCard';
import Timeline from './Timeline';
import EducationPanel from './EducationPanel';
import ContactPanel from './ContactPanel';

interface SectionPanelProps {
  section: Section | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  currentIndex: number;
  totalSections: number;
}

export default function SectionPanel({
  section,
  onClose,
  onPrev,
  onNext,
  currentIndex,
  totalSections,
}: SectionPanelProps) {
  if (!section) return null;

  const { content, color } = section;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={section.id}
        initial={{ opacity: 0, x: 60, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: 60, scale: 0.95 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed z-50 rounded-2xl border backdrop-blur-xl"
        style={{
          right: '16px',
          top: '24px',
          bottom: '24px',
          width: '420px',
          maxWidth: 'calc(100vw - 32px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, rgba(10,10,32,0.92), rgba(5,5,16,0.96))',
          borderColor: `${color}30`,
          boxShadow: `0 0 40px ${color}15, 0 20px 60px rgba(0,0,0,0.5)`,
        }}
      >
        {/* Header — fixed height, never scrolls */}
        <div
          className="p-6 pb-4 border-b"
          style={{ borderColor: `${color}15`, flexShrink: 0 }}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-3">
              <div
                className="w-3 h-3 rounded-full"
                style={{ background: color, boxShadow: `0 0 12px ${color}` }}
              />
              <span className="text-xs font-mono uppercase tracking-widest text-gray-500">
                {String(currentIndex + 1).padStart(2, '0')} / {String(totalSections).padStart(2, '0')}
              </span>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center
                         text-gray-500 hover:text-white transition-colors
                         hover:bg-white/5"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <h2 className="text-2xl font-bold text-white mt-2">{content.heading}</h2>
        </div>

        {/* Scrollable content area */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '24px',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {content.description && !content.contact && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-gray-400 text-sm leading-relaxed whitespace-pre-line"
            >
              {content.description}
            </motion.p>
          )}

          {content.skills && (
            <div className="flex flex-wrap gap-2">
              {content.skills.map((skill, i) => (
                <SkillCard key={skill.name} name={skill.name} category={skill.category} index={i} />
              ))}
            </div>
          )}

          {content.projects && (
            <div>
              {content.projects.map((project, i) => (
                <ProjectCard
                  key={project.title}
                  title={project.title}
                  description={project.description}
                  tags={project.tags}
                  color={color}
                  index={i}
                />
              ))}
            </div>
          )}

          {content.experience && (
            <Timeline items={content.experience} color={color} />
          )}

          {content.education && (
            <EducationPanel
              education={content.education}
              certifications={content.certifications}
              color={color}
            />
          )}

          {content.contact && (
            <ContactPanel contact={content.contact} description={content.description} color={color} />
          )}

          {/* Bottom spacer so last content isn't flush against nav */}
          <div style={{ height: '8px' }} />
        </div>

        {/* Navigation — fixed height, never scrolls */}
        <div
          className="p-4 border-t flex items-center justify-between"
          style={{ borderColor: `${color}15`, flexShrink: 0 }}
        >
          <button
            onClick={onPrev}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm
                       text-gray-400 hover:text-white transition-all
                       hover:bg-white/5"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Prev
          </button>

          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-wider
                       transition-all hover:bg-white/5"
            style={{ color: color }}
          >
            Back to Planet
          </button>

          <button
            onClick={onNext}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm
                       text-gray-400 hover:text-white transition-all
                       hover:bg-white/5"
          >
            Next
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
